import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import GlassCard from '@/components/base/GlassCard';
import KpiCard from '@/components/base/KpiCard';
import { customerLeads, locationLeadData, funnelData } from '@/mocks/dashboardData';

const statusColors: Record<string, { bg: string; color: string }> = {
  'Eşleşme Bekleniyor': { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  'Randevu Oluşturuldu': { bg: 'rgba(16,185,129,0.15)', color: '#10b981' },
  'Tamamlandı': { bg: 'rgba(255,140,66,0.15)', color: '#ff8c42' },
  'İptal Edildi': { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
};

const LeadsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const filtered = customerLeads.filter(l =>
    l.hizmet.toLowerCase().includes(search.toLowerCase()) ||
    l.lokasyon.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Müşteri Talepleri</h1>
        <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>Lead yönetimi ve dönüşüm analizi</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Toplam Lead" value={4800} change={8.2} icon="ri-user-voice-line" />
        <KpiCard label="Dönüşüm Oranı" value={22} change={3.2} icon="ri-exchange-line" suffix="%" />
        <KpiCard label="Bugün" value={87} change={12.1} icon="ri-calendar-line" />
        <KpiCard label="Ortalama Yanıt" value="4.2" icon="ri-time-line" suffix=" dk" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <GlassCard title="Şehir Bazlı Talepler" subtitle="En yüksek talep alan şehirler">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={locationLeadData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="sehir" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} width={60} />
              <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,77,77,0.3)', borderRadius: '8px', fontSize: '12px' }} />
              <Bar dataKey="talep" name="Talep" radius={[0, 4, 4, 0]} fill="url(#leadGrad)" />
              <defs>
                <linearGradient id="leadGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ff4d4d" />
                  <stop offset="100%" stopColor="#ff8c42" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard title="Dönüşüm Funnel" subtitle="Reklam → Tamamlanan">
          <div className="flex items-end justify-center gap-3 h-44">
            {funnelData.map((item, idx) => {
              const maxVal = funnelData[0].sayi;
              const heightPct = (item.sayi / maxVal) * 100;
              return (
                <div key={idx} className="flex flex-col items-center gap-1.5 flex-1">
                  <div className="text-center">
                    <div className="text-white font-bold text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{item.sayi.toLocaleString('tr-TR')}</div>
                    <div className="text-xs" style={{ color: '#6b7280', fontSize: '9px' }}>{idx > 0 ? `%${Math.round((item.sayi / funnelData[0].sayi) * 100)}` : '%100'}</div>
                  </div>
                  <div className="w-full rounded-t-lg" style={{ height: `${Math.max(heightPct * 0.7, 15)}px`, background: `linear-gradient(180deg, ${item.renk} 0%, ${item.renk}88 100%)` }} />
                  <span className="text-center leading-tight" style={{ color: '#9ca3af', fontSize: '9px' }}>{item.aşama}</span>
                </div>
              );
            })}
          </div>
        </GlassCard>
      </div>

      <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Hizmet veya lokasyon ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 rounded-lg text-sm text-white outline-none w-52"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px' }}
            />
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#6b7280' }}></i>
          </div>
          <div className="ml-auto text-xs" style={{ color: '#6b7280' }}>{filtered.length} kayıt</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                {['Hizmet Türü', 'Lokasyon', 'Talep Zamanı', 'Kaynak', 'Durum'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b7280' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead, idx) => (
                <tr key={lead.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="px-4 py-3 text-white font-medium">{lead.hizmet}</td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{lead.lokasyon}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{lead.talep}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: lead.kaynak === 'Reklam' ? 'rgba(255,77,77,0.15)' : 'rgba(16,185,129,0.15)', color: lead.kaynak === 'Reklam' ? '#ff6b47' : '#10b981' }}>{lead.kaynak}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ background: statusColors[lead.durum]?.bg, color: statusColors[lead.durum]?.color }}>{lead.durum}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeadsPage;
